<template>
  <div
    class="ooloja-card-default__body bg-white p-6 rounded-lg shadow-sm border border-gray-200"
  >
    <div class="flex flex-col md:flex-row items-center justify-between mb-4">
      <div class="text-center md:text-left">
        <h3 class="text-lg font-semibold text-gray-800">
          Usuários Inativos (Últimos 90 Dias)
          <small class="block font-normal">
            clientes que não compram há mais de 90 dias, com pelo menos 5 pedidos no
            último ano
          </small>
        </h3>
      </div>
      <div class="flex items-center space-x-2">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar por nome..."
            class="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 hidden md:block"
          />
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <button
          @click="exportToCSV"
          class="bg-green-500 text-white text-sm font-medium py-1.5 px-3 rounded hover:bg-green-600 transition-colors"
        >
          Exportar CSV
        </button>
        <button
          @click="exportToPDF"
          class="bg-red-500 text-white text-sm font-medium py-1.5 px-3 rounded hover:bg-red-600 transition-colors"
        >
          Exportar PDF
        </button>
      </div>
    </div>
    <div class="table-container">
      <table class="w-full border border-gray-200 rounded-lg shadow-sm table-fixed min-w-[500px]">
        <thead class="bg-gray-100 text-gray-800 font-semibold text-sm sticky -top- z-10">
          <tr>
            <th class="py-1 px-4 text-left min-w-[350px] w-full">Nome</th>
            <th class="py-1 px-4 text-center w-[150px] min-w-[150px]">
              Última Compra
              <button
                @click="sortBy('ultima_compra')"
                class="ml-1 text-gray-600 hover:text-gray-800"
              >
                <span
                  :class="{
                    'text-blue-600': sortKey === 'ultima_compra' && sortOrder === 'asc',
                  }"
                  >↑</span
                >
                <span
                  :class="{
                    'text-blue-600': sortKey === 'ultima_compra' && sortOrder === 'desc',
                  }"
                  >↓</span
                >
              </button>
            </th>
            <th class="py-1 px-4 text-center w-[130px] min-w-[130px]">
              Pedidos
              <button
                @click="sortBy('total_pedidos')"
                class="ml-1 text-gray-600 hover:text-gray-800"
              >
                <span
                  :class="{
                    'text-blue-600': sortKey === 'total_pedidos' && sortOrder === 'asc',
                  }"
                  >↑</span
                >
                <span
                  :class="{
                    'text-blue-600': sortKey === 'total_pedidos' && sortOrder === 'desc',
                  }"
                  >↓</span
                >
              </button>
            </th>
            <th class="py-1 px-4 text-center w-[120px] min-w-[120px]">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white">
          <tr
            v-for="(row, index) in filteredUsuarios"
            :key="row.id"
            :class="{
              'bg-gray-50': index % 2 === 0,
              'bg-white': index % 2 !== 0,
              'hover:bg-gray-100': true,
            }"
            class="transition-colors duration-200"
          >
            <td class="py-2 px-4 text-gray-700 text-sm min-w-[150px]">
              {{ row.titulo }}
            </td>
            <td class="py-2 px-4 text-center text-gray-600 text-sm truncate">
              {{ formatDate(row.ultima_compra) }}
            </td>
            <td class="py-2 px-4 text-center text-sm truncate">
              <span
                :class="{
                  'text-green-600 font-semibold': row.total_pedidos >= 20,
                  'text-yellow-600': row.total_pedidos >= 10 && row.total_pedidos < 20,
                  'text-gray-600': row.total_pedidos < 10,
                }"
              >
                {{ row.total_pedidos }}
                <span v-if="row.total_pedidos >= 20" class="ml-1 text-green-600">★</span>
              </span>
            </td>
            <td class="text-center">
              <button
                @click="openModal(row.id)"
                class="bg-blue-500 text-white text-[10px] font-medium py-1 px-3 rounded hover:bg-blue-600 transition-colors"
              >
                Detalhes
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <el-dialog
      v-model="showModal"
      :title="`Detalhes do Cliente`"
      width="90%"
      :max-width="600"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :show-close="true"
      class="custom-dialog max-w-[900px]"
    >
      <div v-if="selectedUser" class="space-y-6">
        <!-- Informações do Perfil -->

        <div
          class="bg-gradient-to-br from-sky-50 to-white border border-gray-200 rounded-2xl shadow-md p-6 w-full mb-6"
        >
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <!-- Coluna Esquerda -->
            <div class="flex items-center gap-4">
              <!-- Avatar icônico -->
              <div
                class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center"
              >
                <svg
                  class="w-7 h-7 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5.121 17.804A6.75 6.75 0 0112 14.25a6.75 6.75 0 016.879 3.554M15 9.75A3 3 0 119 9.75a3 3 0 016 0z"
                  />
                </svg>
              </div>

              <div>
                <h2 class="text-xl font-bold text-gray-800">
                  {{ selectedUser?.perfil[0]?.titulo }}
                </h2>
                <p class="text-sm text-gray-500">
                  Cliente desde
                  <span class="font-medium text-gray-700">{{
                    formatDate(selectedUser.perfil[0]?.datacadastro)
                  }}</span>
                </p>
              </div>
            </div>

            <!-- Coluna Direita -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <span>
                  <p class="font-semibold text-gray-500">Email</p>
                  <a :href="`mailto:${selectedUser.perfil[0]?.email}`">{{
                    selectedUser.perfil[0]?.email
                  }}</a>
                </span>
              </div>
              <div>
                <p class="font-semibold text-gray-500">Telefone</p>
                <p>{{ selectedUser.perfil[0]?.telefone || "N/A" }}</p>
              </div>
              <div>
                <p class="font-semibold text-gray-500">Tipo</p>
                <p>{{ selectedUser.perfil[0]?.tipo_usuario || "N/A" }}</p>
              </div>
              <div>
                <p class="font-semibold text-gray-500">Documento</p>
                <p>
                  {{
                    selectedUser.perfil[0]?.cpf || selectedUser.perfil[0]?.cnpj || "N/A"
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="border p-4 rounded-lg">
          <div class="rounded-xl bg-white shadow-sm w-full">
            <div class="text-center md:text-left">
              <h3 class="text-lg font-semibold text-gray-800">Atividades</h3>
              <small> Dados referentes aos últimos 12 meses </small>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div
                class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
              >
                <span class="text-sm text-gray-500">Valor</span>
                <span class="text-xl font-bold text-green-600">{{
                  selectedUser.atividades[0]?.total_bruto
                    ? `${formatCurrency(selectedUser.atividades[0].total_bruto)}`
                    : "N/A"
                }}</span>
              </div>
              <div
                class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
              >
                <span class="text-sm text-gray-500">Vendas</span>
                <span class="text-xl font-bold text-gray-800">{{
                  selectedUser.atividades[0]?.total_compras || "N/A"
                }}</span>
              </div>
              <div
                class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
              >
                <span class="text-sm text-gray-500">Produtos</span>
                <span class="text-xl font-bold text-gray-800">{{
                  formatarNumeroBR(selectedUser.atividades[0]?.total_produtos_distintos)
                }}</span>
              </div>
              <div
                class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
              >
                <span class="text-sm text-gray-500">Itens</span>
                <span class="text-xl font-bold text-gray-800">{{
                  formatarNumeroBR(selectedUser.atividades[0]?.total_itens)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Produtos Mais Comprados -->
        <div class="border p-4 rounded-lg">
          <div class="flex justify-between mb-6">
            <div class="text-center md:text-left">
              <h3 class="text-lg font-semibold text-gray-800">Produtos Mais Comprados</h3>
              <small>
                Compras do último ano agrupadas por produto e variação, com quantidade
                total e valor faturado
              </small>
            </div>
            <div class="relative">
              <input
                v-model="searchQueryProdutosMaisComprados"
                type="text"
                placeholder="Buscar por nome..."
                class="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 hidden md:block"
              />
              <button
                v-if="searchQueryProdutosMaisComprados"
                @click="clearSearchProdutosMaisComprados"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          <div class="overflow-x-auto max-h-[300px]">
            <table class="w-full border border-gray-200 rounded-lg">
              <thead
                class="bg-gray-100 text-gray-800 font-semibold text-sm sticky -top-1 z-10"
              >
                <tr>
                  <th class="py-2 px-4 text-left">Produto</th>
                  <th class="py-2 px-4 text-center">Quantidade</th>
                  <th class="py-2 px-4 text-center">Total Gasto</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(produto, index) in filteredCompras"
                  :key="index"
                  :class="{ 'bg-gray-50': index % 2 === 0, 'bg-white': index % 2 !== 0 }"
                >
                  <td class="py-2 px-4 text-gray-600 text-sm">
                    {{ produto.titulo || "N/A" }}
                  </td>
                  <td class="py-2 px-4 text-center text-gray-600 text-sm">
                    {{ produto.total_quantidade || "N/A" }}
                  </td>
                  <td class="py-2 px-4 text-center text-gray-600 text-sm">
                    {{
                      produto.total_gasto
                        ? `${formatCurrency(produto.total_gasto)}`
                        : "N/A"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Frequência de Recorrência -->
        <div class="border p-4 rounded-lg">
          <div class="text-center md:text-left mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Frequência de Recorrência</h3>
            <small> Dados referentes aos últimos 12 meses </small>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-center">
            <div
              class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
            >
              <span class="text-sm text-gray-500">Última Venda</span>
              <span class="text-xl font-bold text-green-600">{{
                formatDate(selectedUser.frequenciaRecorrencia[0]?.ultima_compra)
              }}</span>
            </div>
            <div
              class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
            >
              <span class="text-sm text-gray-500">Total de Vendas</span>
              <span class="text-xl font-bold text-gray-800">{{
                selectedUser.frequenciaRecorrencia[0]?.total_compras || "N/A"
              }}</span>
            </div>

            <div
              class="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4 shadow-sm"
            >
              <span class="text-sm text-gray-500">Vendas por Mês</span>
              <span class="text-xl font-bold text-gray-800">{{
                selectedUser.frequenciaRecorrencia[0]?.compras_por_mes
                  ? selectedUser.frequenciaRecorrencia[0].compras_por_mes.toFixed(2)
                  : "N/A"
              }}</span>
            </div>
          </div>

          <!-- <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span class="font-semibold text-gray-700">Última Venda: </span>
              <span class="text-gray-600">{{
                formatDate(selectedUser.frequenciaRecorrencia[0]?.ultima_compra)
              }}</span>
            </div>
            <div>
              <span class="font-semibold text-gray-700">Total de Vendas: </span>
              <span class="text-gray-600">{{
                selectedUser.frequenciaRecorrencia[0]?.total_compras || "N/A"
              }}</span>
            </div>
            <div>
              <span class="font-semibold text-gray-700">Vendas por Mês: </span>
              <span class="text-gray-600">{{
                selectedUser.frequenciaRecorrencia[0]?.compras_por_mes
                  ? selectedUser.frequenciaRecorrencia[0].compras_por_mes.toFixed(2)
                  : "N/A"
              }}</span>
            </div>
          </div> -->
        </div>

        <!-- Produtos por Mês (Tabela e Gráfico) -->
        <div class="border p-4 rounded-lg">
          <h4 class="font-semibold text-lg text-gray-800 mb-2">Produtos por Mês</h4>
          <div class="flex flex-col md:flex-row gap-4">
            <!-- Tabela à esquerda -->
            <div class="w-full md:w-1/2 overflow-x-auto">
              <table class="border border-gray-200 rounded-lg w-full">
                <thead class="bg-gray-100 text-gray-800 font-semibold text-sm">
                  <tr>
                    <th class="py-2 px-4 text-left w-[150px]">Mês</th>
                    <th class="py-2 px-4 text-center w-[200px]">Total de Produtos</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in selectedUser.produtosMes"
                    :key="index"
                    :class="{
                      'bg-gray-50': index % 2 === 0,
                      'bg-white': index % 2 !== 0,
                    }"
                  >
                    <td class="py-2 px-4 text-gray-600 text-sm">
                      {{ formatMonth(item.mes) }}
                    </td>
                    <td class="py-2 px-4 text-center text-gray-600 text-sm">
                      {{ item.total_produtos || "N/A" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Gráfico à direita -->
            <div
              v-if="isClient && chartDataReady && produtosMesChartData?.labels?.length"
              class="w-full md:w-1/2"
            >
              <div class="h-64 chart-container">
                <Bar
                  :data="produtosMesChartData"
                  :chart-options="produtosMesChartOptions"
                  class="h-full"
                />
              </div>
            </div>
            <div v-else class="w-full md:w-1/2 flex items-center justify-center">
              <span class="text-gray-600">Nenhum dado disponível para o gráfico</span>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeModal">Fechar</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from "vue";
import { formatarNumeroBR } from "~/composables/funcoes";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ElDialog, ElButton } from "element-plus";
import { useUserStore } from "~/stores/usuario_store";
import { toast } from "vue3-toastify";

// Registrar os componentes necessários do Chart.js
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const userStore = useUserStore();
const isClient = process.client;

// Props para receber os dados
const props = defineProps({
  usuariosInativos: {
    type: Array,
    required: true,
  },
});

const sortKey = ref("total_pedidos");
const sortOrder = ref("desc");
const searchQuery = ref("");
const searchQueryProdutosMaisComprados = ref("");
const showModal = ref(false);
const selectedUser = ref(null);
const chartDataReady = ref(false); // Novo controle para readiness do gráfico

// Dados e opções do gráfico
const produtosMesChartData = ref({
  labels: [],
  datasets: [
    {
      label: "Total de Produtos",
      data: [],
      backgroundColor: "rgba(59, 130, 246, 0.5)",
      borderColor: "rgba(59, 130, 246, 1)",
      borderWidth: 1,
    },
  ],
});

const produtosMesChartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: "Total de Produtos",
      },
    },
    x: {
      title: {
        display: true,
        text: "Mês",
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
  },
});

// Formatar a data
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

// Função para formatar o mês
const formatMonth = (monthString) => {
  if (!monthString) return "N/A";
  const [year, month] = monthString.split("-");
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1));
};

// Função para atualizar os dados do gráfico
const updateChartData = () => {
  chartDataReady.value = false; // Resetar antes de atualizar

  if (
    !isClient ||
    !selectedUser.value ||
    !Array.isArray(selectedUser.value.produtosMes)
  ) {
    console.warn("Dados do gráfico não disponíveis ou inválidos");
    produtosMesChartData.value = {
      labels: [],
      datasets: [
        {
          label: "Total de Produtos",
          data: [],
          backgroundColor: "rgba(59, 130, 246, 0.5)",
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 1,
        },
      ],
    };
    return;
  }

  try {
    produtosMesChartData.value = {
      labels: selectedUser.value.produtosMes.map((item) => formatMonth(item.mes)),
      datasets: [
        {
          label: "Total de Produtos",
          data: selectedUser.value.produtosMes.map((item) => item.total_produtos || 0),
          backgroundColor: "rgba(59, 130, 246, 0.5)",
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 1,
        },
      ],
    };
    chartDataReady.value = true; // Dados prontos
  } catch (error) {
    console.error("Erro ao atualizar dados do gráfico:", error);
    produtosMesChartData.value = {
      labels: [],
      datasets: [
        {
          label: "Total de Produtos",
          data: [],
          backgroundColor: "rgba(59, 130, 246, 0.5)",
          borderColor: "rgba(59, 130, 246, 1)",
          borderWidth: 1,
        },
      ],
    };
  }
};

// Observar a abertura do modal para atualizar o gráfico
watch(showModal, async (newValue) => {
  if (newValue && isClient && selectedUser.value) {
    await nextTick();
    updateChartData();
  }
});

// Observar mudanças em selectedUser para atualizar o gráfico
watch(selectedUser, async (newValue) => {
  if (newValue && isClient) {
    await nextTick();
    updateChartData();
  }
});

// Ordenação no frontend
const sortedUsuarios = computed(() => {
  const sorted = [...props.usuariosInativos];
  if (sortKey.value) {
    sorted.sort((a, b) => {
      const valueA =
        sortKey.value === "ultima_compra" ? new Date(a[sortKey.value]) : a[sortKey.value];
      const valueB =
        sortKey.value === "ultima_compra" ? new Date(b[sortKey.value]) : b[sortKey.value];
      if (sortOrder.value === "asc") {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });
  }
  return sorted;
});

// Filtrar usuários com base na busca
const filteredUsuarios = computed(() => {
  if (!searchQuery.value) return sortedUsuarios.value;
  const query = searchQuery.value.toLowerCase();
  return sortedUsuarios.value.filter((user) => user.titulo.toLowerCase().includes(query));
});

//filtra compras
const filteredCompras = computed(() => {
  if (!searchQueryProdutosMaisComprados.value)
    return selectedUser.value.produtosMaisComprados;
  const query = searchQueryProdutosMaisComprados.value.toLowerCase();
  return selectedUser.value.produtosMaisComprados.filter((produto) =>
    produto.titulo.toLowerCase().includes(query)
  );
});

// Formatar valores monetários
const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Função para alternar ordenação
const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Limpar o campo de busca
const clearSearch = () => {
  searchQuery.value = "";
};

//limpao campo de busca
const clearSearchProdutosMaisComprados = () => {
  searchQueryProdutosMaisComprados.value = "";
};

// Abrir o modal com os dados do usuário
const openModal = async (userId) => {
  try {
    const response = await useAPI.get(
      `/ooloja/get-UsuarioAll/${userId}/?ac=dashboard`,
      await userStore.user.token
    );
    if (!response.data?.dados) {
      toast.error("Erro ao carregar os dados do usuário", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    selectedUser.value = response.data.dados;
    showModal.value = true;
  } catch (error) {
    console.error("Erro ao abrir modal:", error);
    toast.error("Erro ao carregar os dados do usuário", {
      position: toast.POSITION.TOP_CENTER,
    });
  }
};

// Fechar o modal
const closeModal = () => {
  showModal.value = false;
  selectedUser.value = null;
  chartDataReady.value = false;
  produtosMesChartData.value = {
    labels: [],
    datasets: [
      {
        label: "Total de Produtos",
        data: [],
        backgroundColor: "rgba(59, 130, 246, 0.5)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
      },
    ],
  };
};

// Exportar para CSV
const exportToCSV = () => {
  const headers = ["ID", "Nome", "Última Compra", "Total de Pedidos"];
  const data = filteredUsuarios.value.map((user) => [
    user.id,
    user.titulo,
    formatDate(user.ultima_compra),
    user.total_pedidos,
  ]);

  const csvContent = [
    headers.join(","),
    ...data.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "usuarios_inativos.csv";
  link.click();
};

// Exportar para PDF
const exportToPDF = async () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;
  const systemVersion = "Versão 1.0";
  const website = "www.ooloja.com.br";

  const now = new Date();
  const dateText = now.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  let logoImg = null;
  if (isClient) {
    try {
      const logoUrl = "/ooadmin.png";
      const response = await fetch(logoUrl);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => (reader.onload = resolve));
      logoImg = reader.result;
    } catch (error) {
      console.error("Erro ao carregar logo:", error.message);
    }
  }

  autoTable(doc, {
    head: [["ID", "Nome", "Última Compra", "Total de Pedidos"]],
    body: filteredUsuarios.value.map((user) => [
      user.id,
      user.titulo,
      formatDate(user.ultima_compra),
      user.total_pedidos,
    ]),
    startY: 30,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [100, 116, 139], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [243, 244, 246] },
    didDrawPage: (data) => {
      doc.setFont("Times", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Gerado em: ${dateText}`, margin, 15);

      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text("Usuários Inativos (Últimos 90 Dias)", margin, 22);

      if (logoImg) {
        doc.addImage(logoImg, "PNG", pageWidth - 45 - margin, 7, 45, 15);
      } else {
        doc.setFontSize(10);
        doc.text("[Logo]", pageWidth - 30 - margin, 15);
      }

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const footerText = `${website} - ${systemVersion}`;
      doc.text(footerText, margin, pageHeight - 10);
      const pageStr = `Página ${data.pageNumber} de ${doc.internal.getNumberOfPages()}`;
      doc.text(pageStr, pageWidth - margin - doc.getTextWidth(pageStr), pageHeight - 10);
    },
    margin: { top: 30 },
  });

  doc.save("usuarios_inativos.pdf");
};
</script>

<style scoped>
.table-container {
  @apply mt-4 overflow-x-auto max-h-[300px] overflow-y-auto;
}

th,
td {
  @apply border border-gray-200;
}

th.text-left {
  @apply truncate;
}

td {
  @apply align-middle;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  @apply bg-gray-100;
}

.table-container::-webkit-scrollbar-thumb {
  @apply bg-gray-400 rounded;
}

.table-container::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-500;
}

.table-container {
  -webkit-overflow-scrolling: touch;
}

@media screen and (max-width: 640px) {
  table {
    @apply min-w-[500px];
  }

  th,
  td {
    @apply text-xs px-2;
  }

  th,
  td {
    @apply min-w-[120px];
  }
}

.dialog-footer {
  @apply flex justify-end mt-4;
}

.chart-container {
  position: relative;
  height: 100%;
  width: 100%;
}
</style>
